import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema, schemaBandeiras } from '../validations';
import {
  AdministradoraContextData,
  AdministradorProviderProps,
  FormType,
} from '../types/context';
import { GridRowParams } from '@material-ui/data-grid';
import { bandeiraProps, CidadeParams, rowsProps, SelectType } from '../types';
import { toast } from 'react-toastify';
import { transformAsCurrency, validarCNPJ } from '../../../utils/functions';
import { bandeirasDefault, geralDefault } from '../DefaultData';
import {
  insertOrUpdate,
  addUpdate,
  MasterDetailProps,
} from '~/utils/masterDetail';
import { LojaContext } from '~/context/loja';
import { administradoraServices } from '../services';
import { nanoid } from 'nanoid';
import { FormattedData, Pessoa } from '../types';

export const AdministradoraContext = createContext(
  {} as AdministradoraContextData,
);

export const AdministradoraProvider: React.FC<AdministradorProviderProps> = ({
  children,
}) => {
  const { createAdministradora, updateAdministradora } = administradoraServices;
  const [loader, setLoader] = useState<boolean>(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);

  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [tab, setTab] = useState<string>('geral');
  const [loadingIcon, setLoadingIcon] = useState<string>();
  const [bandeiraId, setBandeiraId] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const masterDetailDefault = [
    {
      obj_name: 'bandeiras',
      pk_fields: ['cod_seq_bandeira'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
  ];
  const [masterDetail, setMasterDetail] =
    useState<MasterDetailProps[]>(masterDetailDefault);
  const { loja } = useContext(LojaContext);
  // cidades
  const [cidade, setCidade] = useState<CidadeParams>();
  const [clearCidade, setClearCidade] = useState<boolean>(false);
  const [cidadeSelected, setCidadeSelected] = useState<SelectType>();

  // bandeiras
  const [rows, setRows] = useState<rowsProps[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { getBandeiras } = administradoraServices;

  // form Geral
  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });
  const formGeral: FormType = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
  };

  // form Bandeiras
  const {
    register: registerBandeira,
    handleSubmit: handleSubmitBandeira,
    control: controlBandeira,
    setValue: setValueBandeira,
    getValues: getValuesBandeira,
    setError: setErrorBandeira,
    setFocus: setFocusBandeira,
    clearErrors: clearErrorsBandeira,
    formState: formStateBandeira,
    watch: watchBandeira,
    reset: resetBandeira,
  } = useForm({
    resolver: yupResolver(schemaBandeiras),
    reValidateMode: 'onBlur',
  });
  const formBandeiras: FormType = {
    register: registerBandeira,
    handleSubmit: handleSubmitBandeira,
    control: controlBandeira,
    setValue: setValueBandeira,
    getValues: getValuesBandeira,
    setError: setErrorBandeira,
    setFocus: setFocusBandeira,
    clearErrors: clearErrorsBandeira,
    formState: formStateBandeira,
    watch: watchBandeira,
    reset: resetBandeira,
  };

  useEffect(() => {
    (async () => {
      if (formState.errors) {
        if (
          formState.errors.des_bairro ||
          formState.errors.num_cep ||
          formState.errors.cidades ||
          formState.errors.des_logradouro ||
          formState.errors.name ||
          formState.errors.num_cpf_cnpj ||
          formState.errors.num_ie ||
          formState.errors.num_endereco
        ) {
          setTab('geral');
        }
      }
    })();
  }, [formState.errors]);

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
  };

  const resetFormData = useCallback(() => {
    setClearCidade(true);
    reset(geralDefault);
    setMasterDetail(JSON.parse(JSON.stringify(masterDetailDefault)));
    resetBandeira(bandeirasDefault);
    setTab('geral');
    setValue('cod_loja', {
      value: loja.cod_loja,
      label: `${loja.des_loja} (${loja.des_cidade})`,
    });
    setCidade(undefined);
    setRows([]);
    setIsEditing(false);
    setClearCidade(false);
  }, [
    reset,
    resetBandeira,
    loja,
    setValue,
    setMasterDetail,
    masterDetailDefault,
  ]);

  const handleAddBandeiras = useCallback(
    async (cod_administradora: number) => {
      const { data, success } = await getBandeiras(cod_administradora);
      if (success) {
        const administradoraObj = data.map((administradora: bandeiraProps) => {
          const uuid = nanoid();
          return {
            uuid,
            ...administradora,
            cod_seq_bandeira: administradora.cod_seq_bandeira,
            cod_loja: {
              value: administradora.cod_loja,
              label: `${administradora.des_loja} (${administradora.des_loja_cidade})`,
            },
            cod_cc: {
              value: administradora.cod_cc,
              label: administradora.descricao,
            },
            per_taxa_credito: transformAsCurrency(
              administradora.per_taxa_credito,
            ),
            num_condicao_credito: administradora.num_condicao_credito,
            per_taxa_debito: transformAsCurrency(
              administradora.per_taxa_debito,
            ),
            num_condicao_debito: administradora.num_condicao_debito,
            per_taxa_parcelado: transformAsCurrency(
              administradora.per_taxa_parcelado,
            ),
            num_condicao_parcela: administradora.num_condicao_parcela,
            flg_remove: false,
          };
        });

        if (!administradoraObj || administradoraObj.length <= 0) return;

        addUpdate(
          'bandeiras',
          administradoraObj,
          masterDetail,
          setMasterDetail,
        );
        setRows(administradoraObj);
      }
    },
    [getBandeiras, masterDetail],
  );

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      const { row } = param;
      const {
        nome_pessoa,
        email,
        num_cpf_cnpj,
        num_endereco,
        num_ie,
        site,
        num_telefone,
        num_celular,
        num_cep,
        des_uf,
        des_logradouro,
        des_contato,
        des_complemento,
        des_cidade,
        des_bairro,
        cod_administradora,
        cod_pessoa,
      } = row;

      reset({
        name: nome_pessoa,
        email,
        num_cnpj: num_cpf_cnpj,
        num_ie,
        num_celular,
        num_telefone,
        num_cep,
        des_bairro,
        des_complemento,
        site,
        num_endereco,
        cod_administradora,
        des_contato,
        des_logradouro,
        cod_pessoa,
      });
      setCidade({
        cidade: des_cidade,
        uf: des_uf,
      });
      handleAddBandeiras(cod_administradora);

      setUpdate(true);
      setLoader(false);
      setShowSearch(false);
    },
    [reset, setCidade, handleAddBandeiras],
  );

  const documentoValido = (doc: string): boolean => {
    const validarDocumento = (
      numDocumento: string,
      mensagemErro: string,
    ): boolean => {
      if (!numDocumento) {
        toast.warn(mensagemErro);
        return false;
      }

      const isValid = validarCNPJ(numDocumento);

      if (!isValid) {
        toast.warn('CNPJ informado é inválido');
      }

      return isValid;
    };

    return validarDocumento(doc, 'CNPJ informado é inválido');
  };

  const prepareData = useCallback(
    (data: any) => {
      const numCnpj = data.num_cnpj;

      const pessoa: Pessoa = {
        cod_pessoa: data.cod_pessoa,
        cod_administradora: data.cod_administradora,
        num_ie: data.num_ie.includes('ISENTO')
          ? data.num_ie
          : data.num_ie.replace(/[^\d]/g, ''),
        flg_administradora_cartao: true,
        nome_pessoa: data.name,
        tipoPessoa: 1, // júridica
        num_cpf_cnpj: numCnpj && numCnpj.replace(/[^\d]/g, ''),
        contato: {
          num_telefone: data.num_telefone,
          num_celular: data.num_celular,
          des_contato: data.des_contato,
          site: data.site,
          email: data.email,
        },
        endereco: {
          num_cep: data.num_cep,
          des_logradouro: data.des_logradouro,
          num_endereco: data.num_endereco,
          des_complemento: data.des_complemento,
          des_bairro: data.des_bairro,
          des_cidade: data.cidades.label.split(' - ')[0],
          cod_cidade: data.cidades.value,
          des_uf: data.cidades.label.split(' - ')[1],
        },
      };

      return {
        pessoa,
        masterDetail,
      };
    },
    [masterDetail],
  );

  const onSubmit = handleSubmit(async (data) => {
    setTab('geral');
    const formattedData: FormattedData = prepareData(data);
    try {
      const isValidCnpj = documentoValido(data.num_cnpj);
      if (!isValidCnpj) {
        setLoadingIcon('');
        return;
      }
      if (!isUpdate) {
        const { message, success } = await createAdministradora(formattedData);
        if (success) {
          toast.success(message);
          resetFormData();
          setShowSearch(false);
        }
        return;
      }
      const { message, success } = await updateAdministradora(
        formattedData.pessoa.cod_administradora,
        formattedData,
      );
      if (success) {
        toast.success(message);
        resetFormData();
        return setShowSearch(true);
      }
    } catch (e: any) {
      if (e.status === 409) {
        const bandeiras = rows.filter((i: rowsProps) =>
          e.data.bandeiras.includes(i.cod_seq_bandeira),
        );

        if (!bandeiras.length) return;

        bandeiras.map(async (bandeira: rowsProps) => {
          const payload: any = {
            ...bandeira,
            flg_remove: false,
          };

          const updateRow: any = await insertOrUpdate(
            'bandeiras',
            payload,
            masterDetail,
            setMasterDetail,
          );

          setRows(updateRow);
        });
      }
    }
  });

  return (
    <AdministradoraContext.Provider
      value={{
        isUpdate,
        setUpdate,
        showSearch,
        setShowSearch,
        formGeral,
        formBandeiras,
        tab,
        setTab,
        loader,
        setLoader,
        loadingTable,
        setLoadingTable,
        resetFormData,
        onRowClick,
        onNew,
        onSubmit,
        setLoadingIcon,
        loadingIcon,
        setCidade,
        cidadeSelected,
        setCidadeSelected,
        clearCidade,
        cidade,
        setClearCidade,
        documentoValido,
        setRows,
        rows,
        isEditing,
        setIsEditing,
        bandeiraId,
        setBandeiraId,
        loja,

        masterDetail,
        setMasterDetail,
      }}
    >
      {children}
    </AdministradoraContext.Provider>
  );
};
